import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import { Check, Sparkles, ExternalLink, ShieldCheck, CreditCard } from "lucide-react";

export default function Pricing() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/plans`)
      .then((r) => r.json())
      .then(setPlans)
      .catch(() => setPlans([]));
  }, []);

  const onCheckout = (p) => {
    if (!user) {
      nav("/login", { state: { from: "/pricing" } });
      return;
    }
    if (p.checkout_url) {
      window.open(p.checkout_url, "_blank", "noopener,noreferrer");
    }
  };

  const Inner = (
    <>
      <div className="mb-10">
        <div className="label-overline flex items-center gap-2">
          <CreditCard size={12} /> {lang === "pt" ? "Planos de Missão" : "Mission Tiers"}
        </div>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mt-2" data-testid="pricing-title">
          {lang === "pt" ? "Escolha sua jornada interestelar" : "Choose your interstellar journey"}
        </h1>
        <p className="text-white/55 mt-3 max-w-2xl text-sm lg:text-base">
          {lang === "pt"
            ? "Pagamento seguro via InfinitePay · Pix, cartão, boleto · Liberação automática após confirmação."
            : "Secure checkout via InfinitePay · Pix, credit card, boleto · Auto-release upon confirmation."}
        </p>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="pricing-grid">
        {plans.map((p) => (
          <div
            key={p.id}
            data-testid={`plan-${p.id}`}
            className={`holo-panel p-6 relative transition-all flex flex-col ${
              p.highlighted
                ? "border-[#FFD27A]/60 shadow-[0_0_40px_rgba(255,210,122,0.18)]"
                : ""
            }`}
          >
            {p.highlighted && (
              <div className="absolute -top-3 left-6 px-2.5 py-1 bg-[#FFD27A] text-[#03040B] font-mono-data text-[10px] tracking-[0.2em] uppercase font-bold"
                   data-testid={`plan-popular-${p.id}`}>
                <Sparkles size={10} className="inline mr-1" />
                {lang === "pt" ? "MAIS POPULAR" : "MOST POPULAR"}
              </div>
            )}

            <div className="font-display font-black text-xl tracking-tight">{p.name}</div>

            <div className="mt-3 flex items-baseline gap-2">
              <div className="font-display font-black text-4xl tracking-tighter" data-testid={`plan-price-${p.id}`}>
                {lang === "pt"
                  ? (p.price_label_brl || `R$ ${p.price_brl?.toLocaleString("pt-BR")}`)
                  : (p.price_label_usd || `$${p.price_usd}`)}
              </div>
              <div className="font-mono-data text-[10px] uppercase tracking-[0.18em] text-white/55">
                {lang === "pt" ? "única vez" : "one-time"}
              </div>
            </div>

            <ul className="mt-6 space-y-2.5 text-sm text-white/85 flex-1" data-testid={`plan-features-${p.id}`}>
              {(lang === "pt" ? p.features_pt : p.features_en).map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check size={14} className={`mt-0.5 shrink-0 ${p.highlighted ? "text-[#FFD27A]" : "text-[#00D4FF]"}`} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => onCheckout(p)}
              data-testid={`plan-cta-${p.id}`}
              className={`mt-6 ${p.highlighted ? "btn-primary-s2s" : "btn-secondary-s2s"} w-full justify-center !py-3 !text-[11px]`}
            >
              <CreditCard size={12} />
              {lang === "pt" ? "PAGAR COM INFINITEPAY" : "PAY WITH INFINITEPAY"}
              <ExternalLink size={11} />
            </button>

            <div className="mt-3 font-mono-data text-[9px] tracking-[0.16em] uppercase text-white/40 text-center">
              {lang === "pt" ? "Pix · Cartão · Boleto" : "Pix · Card · Boleto"}
            </div>
          </div>
        ))}
      </div>

      {/* Reassurance row */}
      <div className="mt-10 grid md:grid-cols-3 gap-4" data-testid="pricing-reassurance">
        <div className="holo-panel p-5 flex items-start gap-3">
          <ShieldCheck size={20} className="text-[#62FFB0] mt-1 shrink-0" />
          <div>
            <div className="font-display font-bold text-sm">
              {lang === "pt" ? "Pagamento Seguro" : "Secure Payment"}
            </div>
            <div className="font-mono-data text-[11px] text-white/55 mt-1 leading-relaxed">
              {lang === "pt"
                ? "Processado pela InfinitePay · criptografia ponta-a-ponta · ativação automática após confirmação."
                : "Processed by InfinitePay · end-to-end encryption · automatic activation upon confirmation."}
            </div>
          </div>
        </div>
        <div className="holo-panel p-5 flex items-start gap-3">
          <Sparkles size={20} className="text-[#FFD27A] mt-1 shrink-0" />
          <div>
            <div className="font-display font-bold text-sm">
              {lang === "pt" ? "Certidão Intergaláctica" : "Intergalactic Certificate"}
            </div>
            <div className="font-mono-data text-[11px] text-white/55 mt-1 leading-relaxed">
              {lang === "pt"
                ? "Certidão luxury de 7 páginas AES-256 + SHA-512, gerada em todas as missões."
                : "7-page luxury certificate AES-256 + SHA-512, generated on every mission."}
            </div>
          </div>
        </div>
        <div className="holo-panel p-5 flex items-start gap-3">
          <CreditCard size={20} className="text-[#00D4FF] mt-1 shrink-0" />
          <div>
            <div className="font-display font-bold text-sm">
              {lang === "pt" ? "Sem assinatura" : "No subscription"}
            </div>
            <div className="font-mono-data text-[11px] text-white/55 mt-1 leading-relaxed">
              {lang === "pt"
                ? "Pague uma vez, transmita quando quiser. Sem cobranças recorrentes."
                : "Pay once, transmit anytime. No recurring charges."}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return user ? (
    <AppShell>{Inner}</AppShell>
  ) : (
    <div className="min-h-screen p-6 lg:p-16 grid-bg">
      <div className="max-w-7xl mx-auto">{Inner}</div>
    </div>
  );
}
